import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { CalendarDays, Clock, Loader2, Users, Ticket, CheckCircle2, Hourglass } from "lucide-react";
import { toast } from "sonner";

import {
  getPublicEvent,
  getEventSignupSummary,
  getMyEventPlace,
  reserveEvent,
  cancelReservation,
  canIReserveEvents,
} from "@/lib/events.functions";
import { formatEventDualTime, detectTimeZone } from "@/lib/events";
import { useMembership } from "@/hooks/useMembership";
import { EventTimeZoneCard } from "@/components/events/EventTimeZoneCard";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import { notifyActivityChange } from "@/lib/activity-events";
import { startMembershipJourney, MEMBERSHIP_JOURNEY_PATH } from "@/lib/funnel";

/** The live member workshop this sign-up page is for. */
const WORKSHOP_SLUG = "attachment-and-relationship-patterns";

const TITLE = "Sign up: Attachment & Relationship Patterns | Recovery Trauma™";
const DESCRIPTION =
  "Reserve your place for the live Recovery Trauma™ member workshop on Sunday 20 September, 4:00 PM UK time. Join the waiting list if every place is taken.";

export const Route = createFileRoute("/workshop-signup")({
  loader: async () => {
    try {
      return await getPublicEvent({ data: { slug: WORKSHOP_SLUG } });
    } catch {
      return null;
    }
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/workshop-signup" }],
  }),
  component: WorkshopSignupPage,
  errorComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <p className="font-display text-2xl text-foreground">We couldn&apos;t load this session</p>
      <p className="mt-2 text-sm text-muted-foreground">Please try again in a moment.</p>
      <Link to="/events" className="btn-gold-outline mt-4 inline-flex text-sm">
        See all live sessions
      </Link>
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <p className="font-display text-2xl text-foreground">Session not found</p>
      <Link to="/events" className="btn-gold-outline mt-4 inline-flex text-sm">
        See all live sessions
      </Link>
    </div>
  ),
});

function WorkshopSignupPage() {
  const seed = Route.useLoaderData();
  const { isAuthed, isMember, loading: authLoading } = useMembership();
  const queryClient = useQueryClient();

  const getPublic = useServerFn(getPublicEvent);
  const getSummary = useServerFn(getEventSignupSummary);
  const getPlace = useServerFn(getMyEventPlace);
  const reserveFn = useServerFn(reserveEvent);
  const cancelFn = useServerFn(cancelReservation);
  const checkAccess = useServerFn(canIReserveEvents);

  const accessQuery = useQuery({
    queryKey: ["event-access"],
    queryFn: () => checkAccess(),
    enabled: isAuthed,
    staleTime: 60_000,
  });
  const canReserve = isMember || accessQuery.data === true;

  const eventQuery = useQuery({
    queryKey: ["event-public", WORKSHOP_SLUG],
    queryFn: () => getPublic({ data: { slug: WORKSHOP_SLUG } }),
    initialData: seed ?? undefined,
  });
  const event = eventQuery.data;

  const summaryQuery = useQuery({
    queryKey: ["event-signup-summary", WORKSHOP_SLUG],
    queryFn: () => getSummary({ data: { slug: WORKSHOP_SLUG } }),
  });

  const placeQuery = useQuery({
    queryKey: ["event-place", event?.id],
    queryFn: () => getPlace({ data: { eventId: event!.id } }),
    enabled: !!event?.id && canReserve,
  });
  const place = placeQuery.data ?? "none";

  const endsAt = event
    ? new Date(event.starts_at).getTime() + (event.duration_minutes ?? 60) * 60_000
    : 0;
  const isPast = !!event && Number.isFinite(endsAt) && endsAt <= Date.now();
  const placesLeft = summaryQuery.data?.placesLeft ?? null;
  const isFull = placesLeft !== null && placesLeft <= 0;

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ["event-signup-summary", WORKSHOP_SLUG] });
    queryClient.invalidateQueries({ queryKey: ["event-place", event?.id] });
    queryClient.invalidateQueries({ queryKey: ["my-reservations"] });
    queryClient.invalidateQueries({ queryKey: ["events"] });
    notifyActivityChange("event");
  };

  const signUp = useMutation({
    mutationFn: () => reserveFn({ data: { eventId: event!.id, timeZone: detectTimeZone() } }),
    onSuccess: (res) => {
      if ((res as { waitlisted?: boolean })?.waitlisted) {
        toast.success("You're on the waiting list 💛", {
          description: "If a place opens before the session, we'll email you the joining details.",
        });
      } else {
        toast.success("Your place is reserved 💛", {
          description: event ? formatEventDualTime(event.starts_at, detectTimeZone()) : undefined,
        });
      }
      refreshAll();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const withdraw = useMutation({
    mutationFn: () => cancelFn({ data: { eventId: event!.id } }),
    onSuccess: () => {
      toast.success(place === "waiting" ? "Removed from the waiting list" : "Your place has been released");
      refreshAll();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!event) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <p className="font-display text-2xl text-foreground">This session isn&apos;t available</p>
        <Link to="/events" className="btn-gold-outline mt-4 inline-flex text-sm">
          See all live sessions
        </Link>
      </div>
    );
  }

  const busy = signUp.isPending || withdraw.isPending;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 pb-24 md:py-14">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold-deep">
        Live member workshop · Sign up
      </p>
      <h1 className="mt-3 font-display text-3xl text-foreground md:text-5xl">{event.title}</h1>
      {event.short_description ? (
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">{event.short_description}</p>
      ) : null}

      <EventTimeZoneCard startsAt={event.starts_at} className="mt-5" />

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <CalendarDays className="h-4 w-4 text-gold-deep" />
          {formatEventDualTime(event.starts_at, detectTimeZone())}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-gold-deep" />
          Approximately {event.duration_minutes ?? 60} minutes
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Users className="h-4 w-4 text-gold-deep" />
          {summaryQuery.isLoading
            ? "Counting places…"
            : placesLeft === null
              ? `${summaryQuery.data?.confirmed ?? 0} members signed up`
              : placesLeft > 0
                ? `${placesLeft} of ${summaryQuery.data?.limit} places left`
                : `All places taken · ${summaryQuery.data?.waiting ?? 0} waiting`}
        </span>
      </div>

      {/* ---- Sign-up panel ---- */}
      <div className="mt-8 rounded-2xl border border-gold/30 bg-card/70 p-5 sm:p-6">
        {isPast ? (
          <p className="text-sm text-muted-foreground">
            This live session has already taken place. Thank you to everyone who joined.
          </p>
        ) : authLoading || (isAuthed && accessQuery.isLoading) ? (
          <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking your membership…
          </p>
        ) : !canReserve ? (
          <>
            <h2 className="font-display text-xl text-foreground">Sign-up is for members</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Live workshops are included with a Recovery Trauma™ membership. You&apos;re welcome to join
              whenever it feels right for you.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link
                to={MEMBERSHIP_JOURNEY_PATH}
                onClick={() => startMembershipJourney("Join Recovery Trauma™", "workshop-signup")}
                className="btn-gold inline-flex text-sm"
              >
                Join Recovery Trauma™
              </Link>
              {!isAuthed ? (
                <Link to="/auth" className="btn-gold-outline inline-flex text-sm">
                  Log in
                </Link>
              ) : null}
            </div>
          </>
        ) : place === "confirmed" ? (
          <>
            <h2 className="inline-flex items-center gap-2 font-display text-xl text-foreground">
              <CheckCircle2 className="h-5 w-5 text-gold-deep" /> You&apos;re signed up 💛
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              We&apos;ve emailed your confirmation, and you&apos;ll get a reminder before we begin. Joining
              details appear on the event page on the day.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link
                to="/events/$slug"
                params={{ slug: WORKSHOP_SLUG }}
                className="btn-gold inline-flex text-sm"
              >
                Open the event page
              </Link>
              <button
                type="button"
                onClick={() => withdraw.mutate()}
                disabled={busy}
                className="btn-gold-outline inline-flex text-sm"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "I can no longer attend"}
              </button>
            </div>
          </>
        ) : place === "waiting" ? (
          <>
            <h2 className="inline-flex items-center gap-2 font-display text-xl text-foreground">
              <Hourglass className="h-5 w-5 text-gold-deep" /> You&apos;re on the waiting list
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Every place is taken for now. If someone releases a place before the session, the longest
              waiting member moves up automatically and we&apos;ll email the joining details. There&apos;s
              no pressure either way — you can step off the list at any time.
            </p>
            <button
              type="button"
              onClick={() => withdraw.mutate()}
              disabled={busy}
              className="btn-gold-outline mt-4 inline-flex text-sm"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Leave the waiting list"}
            </button>
          </>
        ) : (
          <>
            <h2 className="font-display text-xl text-foreground">
              {isFull ? "Join the waiting list" : "Reserve your place"}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {isFull
                ? "Every place is taken. Add your name to the waiting list and we'll email you if a place opens before the session."
                : "Sign up before the day so we can send your joining details and a gentle reminder. You can release your place at any time."}
            </p>
            <button
              type="button"
              onClick={() => signUp.mutate()}
              disabled={busy}
              className="btn-gold mt-4 inline-flex items-center gap-2 text-sm"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Ticket className="h-4 w-4" />
                  {isFull ? "Join the waiting list" : "Save my place"}
                </>
              )}
            </button>
          </>
        )}
      </div>

      <ComplianceNotice className="mt-8" />

      <p className="mt-6 text-center text-sm">
        <Link to="/events" className="text-gold-deep underline-offset-4 hover:underline">
          See all live sessions →
        </Link>
      </p>
    </div>
  );
}
